/**
 * /people/new-joiners — Session 3a PR 3.
 *
 * Replaces the RegistryDrivenPage placeholder with the bespoke
 * New Joiners watch list per Session 3a §4.2.
 *
 * Owning agent: Tashghil.
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel mounted in the right rail (live fetch)
 *   2. InlineAISuggestion cards above the DataTable
 *   3. TraceableValue wrapping onboarding_progress + probation_window
 *      + days_since_hire (≥3 wrappers)
 *
 * CRUD: + Hire Employee primary CTA (routes to M11 wizard) +
 * row-hover Send Reminder / Assign Buddy / Mark Check-in / Edit.
 */

import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronRight,
  FileWarning,
  Plus,
  UserRoundPlus,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { session3aApi, type NewJoinerItem } from "../lib/session3a-api";
import { useLegalEntityGate } from "../lib/legalEntity";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { severityChip, showAlertTrace } from "./_session3aHelpers";

const DAYS_WINDOWS = [30, 60, 90] as const;

export function NewJoinersPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [days, setDays] = useState<number>(30);
  const [filter, setFilter] = useState<
    "all" | "lagging" | "missing_docs" | "no_buddy"
  >("all");

  const list = useQuery({
    queryKey: ["new-joiners", legalEntityId, days],
    queryFn: () => session3aApi.listNewJoiners(legalEntityId ?? "", days),
    enabled: !!legalEntityId,
  });

  const items: NewJoinerItem[] = list.data?.items ?? [];

  const filtered = useMemo(() => {
    if (filter === "lagging") return items.filter((r) => r.onboarding_progress_pct < 80);
    if (filter === "missing_docs") return items.filter((r) => r.missing_documents.length > 0);
    if (filter === "no_buddy") return items.filter((r) => !r.buddy_name && r.days_since_hire > 7);
    return items;
  }, [items, filter]);

  const stats = useMemo(() => {
    const lagging = items.filter((r) => r.onboarding_progress_pct < 80).length;
    const noBuddy = items.filter((r) => !r.buddy_name && r.days_since_hire > 7).length;
    const missingDocs = items.filter((r) => r.missing_documents.length > 0).length;
    const topMissing = (() => {
      const counts: Record<string, number> = {};
      items.forEach((r) =>
        r.missing_documents.forEach((d) => {
          counts[d] = (counts[d] ?? 0) + 1;
        }),
      );
      const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      return top ? { doc: top[0], count: top[1] } : null;
    })();
    return {
      total: items.length,
      lagging,
      noBuddy,
      missingDocs,
      topMissing,
      noBuddyEmployee: items.find((r) => !r.buddy_name && r.days_since_hire > 7),
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("people.new_joiners.title", "New Joiners")}
          subtitle={t(
            "people.new_joiners.subtitle",
            "Last 30 / 60 / 90 days hires with onboarding-progress status and outstanding-document watch. Tashghil tracks the onboarding curve.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {DAYS_WINDOWS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  days === d
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`people.new_joiners.filter.last_${d}_days`, `Last ${d} days`)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setFilter(filter === "lagging" ? "all" : "lagging")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "lagging"
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("people.new_joiners.filter.lagging", "Onboarding < 80%")}
              {stats.lagging > 0 && (
                <span className="ms-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-600 px-1 text-[9px] font-semibold text-white">
                  {stats.lagging}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() =>
                setFilter(filter === "missing_docs" ? "all" : "missing_docs")
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "missing_docs"
                  ? "border-rose-300 bg-rose-50 text-rose-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("people.new_joiners.filter.missing_docs", "Missing documents")}
            </button>
            <button
              type="button"
              onClick={() => setFilter(filter === "no_buddy" ? "all" : "no_buddy")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "no_buddy"
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("people.new_joiners.filter.no_buddy", "Unassigned buddy")}
            </button>
          </div>
          <Link
            to="/people/employees/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("people.new_joiners.cta.hire", "Hire Employee")}
          </Link>
        </div>

        {/* Inline AI suggestions per §3.2 */}
        {stats.noBuddy > 0 && stats.noBuddyEmployee && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<AlertTriangle className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "people.new_joiners.suggestion.buddy_headline",
              `Buddy gap: ${stats.noBuddyEmployee.employee_first_name_en} ${stats.noBuddyEmployee.employee_last_name_en} (Day ${stats.noBuddyEmployee.days_since_hire}) has no buddy assigned.`,
            )}
            body={t(
              "people.new_joiners.suggestion.buddy_body",
              "Tashghil suggests assigning a senior teammate from the same department to accelerate onboarding.",
            )}
            primaryAction={{
              label: t("people.new_joiners.suggestion.assign_buddy", "Assign buddy"),
              onClick: () => {
                showAlertTrace(
                  t(
                    "people.new_joiners.suggestion.assign_buddy_note",
                    "Buddy assignment wiring lands when the employee_buddy table ships. Will route through Tashghil's recommendation engine.",
                  ),
                );
              },
            }}
            suggestionKey={`new-joiners-buddy-${stats.noBuddyEmployee.employee_id}`}
          />
        )}
        {stats.topMissing && stats.topMissing.count >= 2 && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<FileWarning className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "people.new_joiners.suggestion.documents_headline",
              `Document follow-up: ${stats.topMissing.count} joiners missing ${stats.topMissing.doc}.`,
            )}
            body={t(
              "people.new_joiners.suggestion.documents_body",
              "Khidmat can batch-send WhatsApp reminders.",
            )}
            primaryAction={{
              label: t(
                "people.new_joiners.suggestion.send_batch",
                "Send batch reminder",
              ),
              onClick: () =>
                showAlertTrace(
                  t(
                    "people.new_joiners.suggestion.send_batch_note",
                    "Khidmat batch reminder activates with Twilio credential in M8 vault.",
                  ),
                ),
            }}
            suggestionKey={`new-joiners-docs-${stats.topMissing.doc}`}
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">
                    {t("people.new_joiners.col.employee", "Employee")}
                  </th>
                  <th className="px-3 py-2">
                    {t("people.new_joiners.col.hire_date", "Hire date")}
                  </th>
                  <th className="px-3 py-2">
                    {t("people.new_joiners.col.position", "Position")}
                  </th>
                  <th className="px-3 py-2">
                    {t("people.new_joiners.col.onboarding", "Onboarding")}
                  </th>
                  <th className="px-3 py-2">
                    {t("people.new_joiners.col.missing_docs", "Missing documents")}
                  </th>
                  <th className="px-3 py-2">
                    {t("people.new_joiners.col.probation", "Probation")}
                  </th>
                  <th className="px-3 py-2">
                    {t("people.new_joiners.col.buddy", "Buddy")}
                  </th>
                  <th className="px-3 py-2">
                    {t("common.actions", "Actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.employee_id}
                    className="group border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_first_name_en} {row.employee_last_name_en}
                      </Link>
                      <div className="text-xs text-text-tertiary">{row.employee_code}</div>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      <div dir="ltr">{row.hire_date}</div>
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "people.new_joiners.trace.days_since_hire",
                              `days_since_hire = today − hire_date (${row.hire_date}). Calendar days per Saudi Labor Law convention.`,
                            ),
                          )
                        }
                      >
                        <span className="text-xs text-text-tertiary">
                          {row.days_since_hire}{" "}
                          {t("people.new_joiners.days_ago", "days ago")}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      <div>{row.position_title ?? "—"}</div>
                      <div className="text-xs text-text-tertiary">
                        {[row.department_name, row.branch_name]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "people.new_joiners.trace.onboarding",
                              `onboarding_progress_pct = completed_checklist_items / total. Sourced from M27 onboarding_state JSONB per employee.`,
                            ),
                          )
                        }
                      >
                        <div className="flex items-center gap-1.5">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-200">
                            <div
                              className="h-full bg-gradient-to-r from-[#00D3C2] via-[#2260C9] to-[#A846BF]"
                              style={{ width: `${row.onboarding_progress_pct}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-text-secondary">
                            {row.onboarding_progress_pct}%
                          </span>
                        </div>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      {row.missing_documents.length >= 2 ? (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip("critical")}`}>
                          {row.missing_documents.join(" · ")}
                        </span>
                      ) : row.missing_documents.length === 1 ? (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip("attention")}`}>
                          {row.missing_documents[0]}
                        </span>
                      ) : (
                        <span className="text-xs text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "people.new_joiners.trace.probation",
                              `probation_window = hire_date + probation_period_days from contract. Article 53 caps at 90 days base / 180 with consent.`,
                            ),
                          )
                        }
                      >
                        <span className="text-xs text-text-secondary">
                          {row.probation_days_elapsed} / {row.probation_days_total}{" "}
                          {t("people.new_joiners.days", "days")}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      {row.buddy_name ? (
                        <span className="text-text-secondary">{row.buddy_name}</span>
                      ) : (
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            row.days_since_hire > 7
                              ? severityChip("attention")
                              : severityChip("neutral")
                          }`}
                        >
                          {t("people.new_joiners.unassigned", "Unassigned")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                        <Link
                          to={`/people/employees/${row.employee_id}`}
                          className="rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                        >
                          {t("people.new_joiners.action.open", "Open")}
                          <ChevronRight className="ms-0.5 inline h-3 w-3" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            <UserRoundPlus className="mx-auto mb-2 h-6 w-6 text-text-tertiary" />
            {t(
              "people.new_joiners.empty",
              `No new hires in the last ${days} days. ✦ Tashghil suggests opening the Hire Employee wizard to start onboarding.`,
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: stats.total,
            attention: stats.lagging,
            critical: stats.noBuddy,
            top_kind: t("people.new_joiners.title", "New Joiners"),
            window_label: `last ${days} days`,
          }}
        />
      </aside>
    </div>
  );
}
